import React, { useId, useRef } from 'react';
import { HiOutlineX } from 'react-icons/hi';
import { SelectOption } from 'src/components/admin/form/selectField';
import { useFocusTrap } from 'hooks/useFocusTrap';
import { useEscapeKey } from 'hooks/useEscapeKey';
import { useTranslation } from 'react-i18next';

interface ShowMoreMultipleItemsModalProps {
   isOpen: boolean;
   onClose: () => void;
   options: SelectOption[];
   onRemove: (value: string) => void;
   title?: string;
}

const ShowMoreMultipleItemsModal: React.FC<ShowMoreMultipleItemsModalProps> = ({ isOpen, onClose, options, onRemove, title = 'Selected Items' }) => {
   const { t } = useTranslation();
   const modalRef = useRef<HTMLDivElement>(null);
   const titleId = useId();

   useFocusTrap(isOpen, modalRef);
   useEscapeKey(isOpen, onClose, modalRef);

   if (!isOpen) return null;

   return (
      <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black bg-opacity-50">
         <div
            ref={modalRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
            className="bg-white rounded-lg shadow-xl w-[800px] max-w-[95vw] h-[70vh] flex flex-col overflow-hidden"
         >
            <div className="flex justify-between items-center p-4 border-b border-gray-200">
               <h3 id={titleId} className="text-lg font-bold">
                  {title} ({options.length})
               </h3>
               <button aria-label={t('a11y.closeDialog')} onClick={onClose} className="p-1 hover:bg-gray-100 rounded-full">
                  <HiOutlineX size={20} className="text-gray-600" />
               </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4">
               {options.length === 0 ? (
                  <p className="text-gray-500 text-sm">No items selected.</p>
               ) : (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                     {options.map(option => (
                        <div key={option.value} className="flex items-center justify-between bg-lightGray rounded text-l-sm py-1.75 px-1.5">
                           <div className="flex items-center gap-2 min-w-0">
                              {option.HtmlInputLabel ? (
                                 <option.HtmlInputLabel />
                              ) : (
                                 <span className="truncate whitespace-nowrap" title={option.label}>
                                    {option.label}
                                 </span>
                              )}
                           </div>
                           <button
                              type="button"
                              aria-label={t('a11y.removeItem', { label: option.label })}
                              className="ml-1 px-1.25 py-0.5 hover:bg-gray-200 rounded flex items-center justify-center shrink-0"
                              onClick={() => onRemove(option.value)}
                           >
                              <HiOutlineX size={10} className="text-black pointer-events-none" />
                           </button>
                        </div>
                     ))}
                  </div>
               )}
            </div>
         </div>
      </div>
   );
};

export default ShowMoreMultipleItemsModal;
